/**
 * Feature registry.
 *
 * Feature entries are imported **statically** and their pages are loaded lazily
 * (see `routeHelpers.tsx`). This is deliberate and load-bearing:
 *
 * The previous design dynamically imported all 11 feature entries and `await`ed
 * them in `main.tsx` before `root.render()`. Because 10 of the 11 entries
 * imported their pages statically, that awaited every feature's entire page
 * graph before first paint — measured at ~1.5 MB across 11 chunks, including a
 * 156 KB markdown renderer for `/docs` on every session, in a two-hop waterfall.
 * Route-level code splitting existed but was nullified at startup.
 *
 * Now: a feature entry contributes only its nav manifest (a few strings plus
 * already-bundled lucide icons) and `lazy()` route factories, so nav renders
 * synchronously and each page chunk is fetched on navigation.
 *
 * INVARIANT: a feature entry may import only react, react-router-dom,
 * lucide-react, `@/components/ProtectedRoute`, `./routeHelpers`, `@shared/*`
 * types, and `lazy(() => import('./pages/*'))` factories. Anything heavier
 * silently re-inflates the entry chunk. `architecture.test.ts` enforces this.
 */
import { isFeatureEnabled, isDevFeature, type FeatureName } from '@shared/features'
import { isProduction } from '@/utils/env'
import type { NavItemConfig } from '@/components/layout/NavItem'
import type { FrontendFeature } from './types'
import type { ReactElement } from 'react'

import aboutFeature from './about'
import adminFeature from './admin'
import catalogFeature from './catalog'
import contactsFeature from './contacts'
import dashboardFeature from './dashboard'
import devtoolsFeature from './devtools'
import docsFeature from './docs'
import filesFeature from './files'
import inventoryFeature from './inventory'
import manufacturerSettingsFeature from './manufacturer-settings'
import profileFeature from './profile'
import projectsFeature from './projects'

const prod = isProduction()

/**
 * Feature-flag key → frontend feature module.
 *
 * Declaration order defines nav order for the `admin`/`insights` groups; the
 * `workspace` group is re-sorted by `WORKSPACE_ORDER` in `AppLayout`.
 * Not every flag has a frontend module — many are backend-only.
 */
const modules: Partial<Record<FeatureName, FrontendFeature>> = {
  // Declared first so `/dashboard` — the eventual default landing route — is registered
  // before the catch-all in `App.tsx`. It contributes routes only; its nav entry comes
  // from `AppLayout`'s `coreNavItems`.
  dashboard: dashboardFeature,
  admin: adminFeature,
  profile: profileFeature,
  docs: docsFeature,
  devtools: devtoolsFeature,
  files: filesFeature,
  about: aboutFeature,
  products: catalogFeature,
  inventory: inventoryFeature,
  manufacturerSettings: manufacturerSettingsFeature,
  contacts: contactsFeature,
  projects: projectsFeature,
  // NOTE: there is no `projectOperations` entry. That feature exported
  // `{navItems: [], routes: []}` — it registered nothing, so loading it only cost
  // a chunk fetch. Its code is a projects sub-domain and now lives at
  // `features/projects/operations/`; the `/admin/report-templates` route it
  // provided is declared by the admin feature, which owns the `/admin/*` prefix.
}

/** Enabled features, in declaration order. Computed once at module load. */
const enabledFeatures: { name: FeatureName; feature: FrontendFeature }[] = (
  Object.entries(modules) as [FeatureName, FrontendFeature | undefined][]
)
  .filter((entry): entry is [FeatureName, FrontendFeature] => entry[1] !== undefined)
  .filter(([name]) => isFeatureEnabled(name, prod))
  .map(([name, feature]) => ({ name, feature }))

/** Nav items for core features (Dashboard area) */
export function getFeatureNavItems(): NavItemConfig[] {
  return enabledFeatures
    .filter(({ name }) => !isDevFeature(name))
    .flatMap(({ feature }) => feature.navItems)
}

/** Nav items for dev testing features (Development Testing area) */
export function getDevFeatureNavItems(): NavItemConfig[] {
  return enabledFeatures
    .filter(({ name }) => isDevFeature(name))
    .flatMap(({ feature }) => feature.navItems)
}

/** All routes from all enabled features */
export function getFeatureRoutes(): ReactElement[] {
  return enabledFeatures.flatMap(({ feature }) => feature.routes)
}
